import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import EditIcon from "@mui/icons-material/Edit";
import {
  FaCloud,
  FaExternalLinkAlt,
  FaSync,
} from "react-icons/fa";
import { BACKEND } from "../config/configEnvs";
import { months, years } from "../utils/rawData/dateValues";
import { canAccessPagosRecurrentes } from "../utils/canAccessPagosRecurrentes";
import GenerarInstanciasPagosRecurrentes from "../utils/automateFuncions/GenerarInstanciasPagosRecurrentes";
import { formatProgramacionPago } from "../utils/fechaPagoRecurrente";
import LoadingScreen from "../components/ui/temp-screens/LoadingScreen";
import Modal from "../components/ui/Modal";
import DeleteButton from "../components/ui/components/DeleteButton";

const emptyPlantilla = {
  nombre: "",
  montoEstimado: "",
  moneda: "soles",
  tipoMonto: "fijo",
  frecuencia: "mensual",
  mesPago: "",
  diaProgramado: "",
  desfaseMes: 1,
  linkVerificacion: "",
  activo: true,
  notas: "",
};

const monedaLabel = { soles: "S/.", dolares: "$" };

function PagosRecurrentes() {
  const user = useSelector((state) => state.auth.user);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [plantillas, setPlantillas] = useState([]);
  const [instancias, setInstancias] = useState([]);
  const [modalPlantilla, setModalPlantilla] = useState(false);
  const [editingPlantilla, setEditingPlantilla] = useState(null);
  const [formPlantilla, setFormPlantilla] = useState(emptyPlantilla);
  const [saving, setSaving] = useState(false);

  const fetchInstancias = useCallback(async () => {
    const instanciasRes = await axios.get(
      `${BACKEND}/api/instanciasPagoRecurrente?limit=0&where[month][equals]=${month}&where[year][equals]=${year}&depth=1&sort=fechaPagoExacta`,
      { withCredentials: true }
    );
    setInstancias(instanciasRes.data.docs || []);
  }, [month, year]);

  const fetchPlantillas = useCallback(async () => {
    const plantillasRes = await axios.get(
      `${BACKEND}/api/pagosRecurrentes?limit=0&sort=nombre`,
      { withCredentials: true }
    );
    setPlantillas(plantillasRes.data.docs || []);
  }, []);

  const fetchData = useCallback(
    async ({ showLoader = true } = {}) => {
      try {
        if (showLoader) setLoading(true);
        await Promise.all([fetchPlantillas(), fetchInstancias()]);
      } catch (error) {
        console.error(error);
        toast.error("Error al cargar pagos recurrentes");
      } finally {
        if (showLoader) setLoading(false);
      }
    },
    [fetchPlantillas, fetchInstancias]
  );

  useEffect(() => {
    if (canAccessPagosRecurrentes(user)) {
      fetchData();
    }
  }, [fetchData, user]);

  if (!canAccessPagosRecurrentes(user)) {
    return <Navigate to="/home" replace />;
  }

  const openNewPlantilla = () => {
    setEditingPlantilla(null);
    setFormPlantilla(emptyPlantilla);
    setModalPlantilla(true);
  };

  const deletePlantilla = async (p) => {
    const confirmed = window.confirm(
      `¿Eliminar "${p.nombre}"? También se borrarán todas sus instancias mensuales.`
    );
    if (!confirmed) return;

    try {
      const instRes = await axios.get(
        `${BACKEND}/api/instanciasPagoRecurrente?limit=0&where[pagoRecurrente][equals]=${p.id}&depth=0`,
        { withCredentials: true }
      );
      await Promise.all(
        (instRes.data.docs || []).map((inst) =>
          axios.delete(
            `${BACKEND}/api/instanciasPagoRecurrente/${inst.id}`,
            { withCredentials: true }
          )
        )
      );
      await axios.delete(`${BACKEND}/api/pagosRecurrentes/${p.id}`, {
        withCredentials: true,
      });
      setPlantillas((prev) => prev.filter((x) => x.id !== p.id));
      setInstancias((prev) =>
        prev.filter((i) => {
          const prId =
            typeof i.pagoRecurrente === "object"
              ? i.pagoRecurrente?.id
              : i.pagoRecurrente;
          return prId !== p.id;
        })
      );
      toast.success("Pago recurrente eliminado");
    } catch (error) {
      console.error(error);
      toast.error("Error al eliminar el pago recurrente");
    }
  };

  const openEditPlantilla = (p) => {
    setEditingPlantilla(p);
    setFormPlantilla({
      nombre: p.nombre || "",
      montoEstimado: p.montoEstimado ?? "",
      moneda: p.moneda || "soles",
      tipoMonto: p.tipoMonto || "fijo",
      frecuencia: p.frecuencia || "mensual",
      mesPago: p.mesPago ?? "",
      diaProgramado: p.diaProgramado ?? "",
      desfaseMes: p.desfaseMes ?? 0,
      linkVerificacion: p.linkVerificacion || "",
      activo: p.activo !== false,
      notas: p.notas || "",
    });
    setModalPlantilla(true);
  };

  const savePlantilla = async () => {
    if (!formPlantilla.nombre.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    const dia = Number(formPlantilla.diaProgramado);
    if (!dia || dia < 1 || dia > 31) {
      toast.error("El día programado debe ser entre 1 y 31");
      return;
    }

    const frecuencia = formPlantilla.frecuencia || "mensual";
    const mesPago = Number(formPlantilla.mesPago);
    if (frecuencia === "anual") {
      if (!mesPago || mesPago < 1 || mesPago > 12) {
        toast.error("El mes de pago es obligatorio para pagos anuales (1-12)");
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        ...formPlantilla,
        montoEstimado: Number(formPlantilla.montoEstimado) || 0,
        diaProgramado: dia,
        frecuencia,
        mesPago: frecuencia === "anual" ? mesPago : null,
        desfaseMes: frecuencia === "mensual" ? Number(formPlantilla.desfaseMes) || 0 : 0,
      };

      if (editingPlantilla) {
        await axios.patch(
          `${BACKEND}/api/pagosRecurrentes/${editingPlantilla.id}`,
          payload,
          { withCredentials: true }
        );
        toast.success("Pago recurrente actualizado");
      } else {
        await axios.post(`${BACKEND}/api/pagosRecurrentes`, payload, {
          withCredentials: true,
        });
        toast.success("Pago recurrente registrado");
      }
      setModalPlantilla(false);
      await fetchData({ showLoader: false });
    } catch (error) {
      console.error(error);
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const patchInstancia = async (instancia, changes) => {
    const snapshot = { ...instancia };
    setInstancias((prev) =>
      prev.map((i) => (i.id === instancia.id ? { ...i, ...changes } : i))
    );
    try {
      await axios.patch(
        `${BACKEND}/api/instanciasPagoRecurrente/${instancia.id}`,
        changes,
        { withCredentials: true }
      );
    } catch (error) {
      setInstancias((prev) =>
        prev.map((i) => (i.id === instancia.id ? snapshot : i))
      );
      console.error(error);
      toast.error("Error al actualizar instancia");
    }
  };

  const handleEstadoChange = (instancia, nuevoEstado) => {
    if (nuevoEstado === instancia.estadoPago) return;
    patchInstancia(instancia, {
      estadoPago: nuevoEstado,
      fechaPagoReal:
        nuevoEstado === "Pagado" ? new Date().toISOString() : null,
    });
  };

  const handleMontoChange = (instancia, val) => {
    if (val === instancia.montoReal) return;
    patchInstancia(instancia, { montoReal: val });
  };

  const deleteInstancia = async (instancia) => {
    const nombre = getPlantillaNombre(instancia);
    const confirmed = window.confirm(
      `¿Eliminar la instancia de "${nombre}" para ${months.find((m) => m.value === month)?.label} ${year}?`
    );
    if (!confirmed) return;

    const snapshot = instancias;
    setInstancias((prev) => prev.filter((i) => i.id !== instancia.id));

    try {
      await axios.delete(
        `${BACKEND}/api/instanciasPagoRecurrente/${instancia.id}`,
        { withCredentials: true }
      );
      toast.success("Instancia eliminada");
    } catch (error) {
      setInstancias(snapshot);
      console.error(error);
      toast.error("Error al eliminar la instancia");
    }
  };

  const resumen = {
    total: instancias.length,
    pagados: instancias.filter((i) => i.estadoPago === "Pagado").length,
    pendientes: instancias.filter((i) => i.estadoPago === "Pendiente").length,
    montoPagado: instancias
      .filter((i) => i.estadoPago === "Pagado")
      .reduce((acc, i) => acc + (i.montoReal || 0), 0),
  };

  const getPlantillaNombre = (instancia) => {
    const pr = instancia.pagoRecurrente;
    return typeof pr === "object" ? pr?.nombre : "—";
  };

  const getPlantillaLink = (instancia) => {
    const pr = instancia.pagoRecurrente;
    return typeof pr === "object" ? pr?.linkVerificacion : null;
  };

  return (
    <div className="flex flex-col h-screen w-full">
      <Modal
        activeModal={modalPlantilla}
        onClose={() => setModalPlantilla(false)}
        title={
          editingPlantilla ? "Editar pago recurrente" : "Nuevo pago recurrente"
        }
        centered
      >
        <div className="flex flex-col gap-3 p-2 min-w-[320px]">
          <label className="text-sm font-medium text-gray-700">
            Nombre *
            <input
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={formPlantilla.nombre}
              onChange={(e) =>
                setFormPlantilla({ ...formPlantilla, nombre: e.target.value })
              }
              placeholder="AWS, Google Cloud..."
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm font-medium text-gray-700">
              Monto estimado
              <input
                type="number"
                min="0"
                step="0.01"
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={formPlantilla.montoEstimado}
                onChange={(e) =>
                  setFormPlantilla({
                    ...formPlantilla,
                    montoEstimado: e.target.value,
                  })
                }
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              Moneda
              <select
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={formPlantilla.moneda}
                onChange={(e) =>
                  setFormPlantilla({
                    ...formPlantilla,
                    moneda: e.target.value,
                  })
                }
              >
                <option value="soles">Soles</option>
                <option value="dolares">Dólares</option>
              </select>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm font-medium text-gray-700">
              Tipo de pago
              <select
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={formPlantilla.tipoMonto}
                onChange={(e) =>
                  setFormPlantilla({
                    ...formPlantilla,
                    tipoMonto: e.target.value,
                  })
                }
              >
                <option value="fijo">Pago fijo</option>
                <option value="variable">Pago variable</option>
              </select>
            </label>
            <label className="text-sm font-medium text-gray-700">
              Frecuencia *
              <select
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={formPlantilla.frecuencia}
                onChange={(e) =>
                  setFormPlantilla({
                    ...formPlantilla,
                    frecuencia: e.target.value,
                  })
                }
              >
                <option value="mensual">Mensual</option>
                <option value="anual">Anual</option>
              </select>
            </label>
            {formPlantilla.frecuencia === "anual" && (
              <label className="text-sm font-medium text-gray-700">
                Mes de pago (1-12) *
                <select
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={formPlantilla.mesPago}
                  onChange={(e) =>
                    setFormPlantilla({
                      ...formPlantilla,
                      mesPago: Number(e.target.value),
                    })
                  }
                >
                  <option value="">Seleccionar mes</option>
                  {months.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="text-sm font-medium text-gray-700">
              Día de pago (1-31) *
              <input
                type="number"
                min="1"
                max="31"
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={formPlantilla.diaProgramado}
                onChange={(e) =>
                  setFormPlantilla({
                    ...formPlantilla,
                    diaProgramado: e.target.value,
                  })
                }
              />
            </label>
            {formPlantilla.frecuencia === "mensual" && (
              <label className="text-sm font-medium text-gray-700">
                Mes de pago (respecto al período)
                <select
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={formPlantilla.desfaseMes}
                  onChange={(e) =>
                    setFormPlantilla({
                      ...formPlantilla,
                      desfaseMes: Number(e.target.value),
                    })
                  }
                >
                  <option value={0}>Mismo mes del período</option>
                  <option value={1}>Mes siguiente al período</option>
                  <option value={2}>2 meses después del período</option>
                </select>
                <span className="text-xs text-gray-500 font-normal block mt-1">
                  Ej: período mayo + mes siguiente + día 1 → vence 1/junio
                </span>
              </label>
            )}
          </div>
          {formPlantilla.frecuencia === "anual" && (
            <p className="text-xs text-gray-500 -mt-1">
              El monto estimado es el total anual. Solo se generará una instancia
              en el mes de pago seleccionado.
            </p>
          )}
          <label className="text-sm font-medium text-gray-700">
            Link de verificación
            <input
              type="url"
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={formPlantilla.linkVerificacion}
              onChange={(e) =>
                setFormPlantilla({
                  ...formPlantilla,
                  linkVerificacion: e.target.value,
                })
              }
              placeholder="https://..."
            />
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={formPlantilla.activo}
              onChange={(e) =>
                setFormPlantilla({
                  ...formPlantilla,
                  activo: e.target.checked,
                })
              }
            />
            Activo (participa en generación automática)
          </label>
          <label className="text-sm font-medium text-gray-700">
            Notas
            <textarea
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              rows={2}
              value={formPlantilla.notas}
              onChange={(e) =>
                setFormPlantilla({ ...formPlantilla, notas: e.target.value })
              }
            />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outlined" onClick={() => setModalPlantilla(false)}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              disabled={saving}
              onClick={savePlantilla}
              sx={{
                backgroundColor: "#ff6400",
                "&:hover": { backgroundColor: "#bb4a03" },
                textTransform: "none",
              }}
            >
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </div>
        </div>
      </Modal>

      <div className="flex flex-row py-2 bg-gray-100">
        <div className="flex px-4 w-full items-center gap-4 justify-between overflow-x-auto">
          <div className="flex items-center gap-4 p-4">
            <FaCloud className="text-2xl text-gray-700" />
            <h2 className="text-2xl font-bold text-gray-700">
              Pagos recurrentes
            </h2>
          </div>
          <div className="flex flex-row gap-3 items-center flex-wrap justify-end py-2">
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="text-xs border border-gray-300 rounded-lg px-2 py-2 bg-white"
            >
              {months.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="text-xs border border-gray-300 rounded-lg px-2 py-2 bg-white"
            >
              {years.map((y) => (
                <option key={y.value} value={y.value}>
                  {y.label}
                </option>
              ))}
            </select>
            <Button
              variant="outlined"
              size="small"
              onClick={fetchData}
              startIcon={<FaSync />}
              sx={{ textTransform: "none" }}
            >
              Actualizar
            </Button>
            <Button
              variant="contained"
              onClick={() =>
                GenerarInstanciasPagosRecurrentes(month, year, fetchInstancias)
              }
              endIcon={<AutoFixHighIcon />}
              sx={{
                fontSize: "12px",
                backgroundColor: "#ff6400",
                textTransform: "none",
                "&:hover": { backgroundColor: "#bb4a03" },
              }}
            >
              Generar instancias del mes
            </Button>
            <Button
              variant="contained"
              onClick={openNewPlantilla}
              endIcon={<AddIcon />}
              sx={{
                fontSize: "12px",
                backgroundColor: "#ff6400",
                textTransform: "none",
                "&:hover": { backgroundColor: "#bb4a03" },
              }}
            >
              Nuevo pago recurrente
            </Button>
          </div>
        </div>
      </div>

      <div className="flex bg-gray-50 px-6 py-2 gap-6 text-xs text-gray-700 flex-wrap">
        <span>
          Instancias: <strong>{resumen.total}</strong>
        </span>
        <span className="text-green-700">
          Pagadas: <strong>{resumen.pagados}</strong>
        </span>
        <span className="text-red-600">
          Pendientes: <strong>{resumen.pendientes}</strong>
        </span>
        <span>
          Monto pagado (instancias marcadas):{" "}
          <strong>S/. {resumen.montoPagado.toFixed(2)}</strong>
        </span>
      </div>

      {loading ? (
        <LoadingScreen />
      ) : (
        <div className="flex-1 overflow-auto p-4 flex flex-col gap-6">
          <section>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Plantillas ({plantillas.length})
            </h3>
            <div className="overflow-x-auto border border-gray-200 rounded-lg bg-white">
              <table className="w-full table-fixed text-sm">
                <colgroup>
                  <col style={{ width: "22%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "14%" }} />
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "14%" }} />
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "8%" }} />
                </colgroup>
                <thead className="bg-gray-100 text-gray-700">
                  <tr>
                    <th className="px-3 py-2 text-left">Nombre</th>
                    <th className="px-3 py-2 text-left">Tipo</th>
                    <th className="px-3 py-2 text-left">Monto est.</th>
                    <th className="px-3 py-2 text-left">Programación</th>
                    <th className="px-3 py-2 text-left">Link</th>
                    <th className="px-3 py-2 text-left">Activo</th>
                    <th className="px-3 py-2 text-left">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {plantillas.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                        No hay pagos recurrentes registrados
                      </td>
                    </tr>
                  ) : (
                    plantillas.map((p) => (
                      <tr key={p.id} className="border-t border-gray-100">
                        <td className="px-3 py-2 font-medium truncate">{p.nombre}</td>
                        <td className="px-3 py-2 capitalize truncate">{p.tipoMonto}</td>
                        <td className="px-3 py-2 truncate">
                          {monedaLabel[p.moneda] || ""}{" "}
                          {(p.montoEstimado ?? 0).toFixed(2)}
                        </td>
                        <td className="px-3 py-2 truncate">
                          {formatProgramacionPago(p)}
                        </td>
                        <td className="px-3 py-2 truncate">
                          {p.linkVerificacion ? (
                            <a
                              href={p.linkVerificacion}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 inline-flex items-center gap-1"
                            >
                              Ver <FaExternalLinkAlt className="text-xs" />
                            </a>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={
                              p.activo
                                ? "text-green-600 font-medium"
                                : "text-gray-400"
                            }
                          >
                            {p.activo ? "Sí" : "No"}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => openEditPlantilla(p)}
                              className="text-gray-600 hover:text-orange-600 p-1"
                              title="Editar"
                            >
                              <EditIcon fontSize="small" />
                            </button>
                            <DeleteButton
                              content="Eliminar pago recurrente"
                              action={() => deletePlantilla(p)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Instancias — {months.find((m) => m.value === month)?.label}{" "}
              {year}
            </h3>
            <div className="overflow-x-auto border border-gray-200 rounded-lg bg-white">
              <table className="w-full table-fixed text-sm">
                <colgroup>
                  <col style={{ width: "24%" }} />
                  <col style={{ width: "11%" }} />
                  <col style={{ width: "15%" }} />
                  <col style={{ width: "13%" }} />
                  <col style={{ width: "11%" }} />
                  <col style={{ width: "17%" }} />
                  <col style={{ width: "9%" }} />
                </colgroup>
                <thead className="bg-gray-100 text-gray-700">
                  <tr>
                    <th className="px-3 py-2 text-left">Servicio</th>
                    <th className="px-3 py-2 text-left">Vence</th>
                    <th className="px-3 py-2 text-left">Monto real</th>
                    <th className="px-3 py-2 text-left">Estado</th>
                    <th className="px-3 py-2 text-left">Verificar</th>
                    <th className="px-3 py-2 text-left">Fecha pago</th>
                    <th className="px-3 py-2 text-left">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {instancias.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                        No hay instancias para este mes. Usa &quot;Generar instancias del mes&quot;.
                      </td>
                    </tr>
                  ) : (
                    instancias.map((inst) => {
                      const pr =
                        typeof inst.pagoRecurrente === "object"
                          ? inst.pagoRecurrente
                          : null;
                      const moneda = pr?.moneda || "soles";
                      return (
                        <tr key={inst.id} className="border-t border-gray-100">
                          <td className="px-3 py-2 font-medium truncate">
                            {getPlantillaNombre(inst)}
                            {pr?.frecuencia === "anual" && (
                              <span className="ml-1 text-xs text-blue-600">
                                (anual)
                              </span>
                            )}
                            {pr?.tipoMonto === "variable" && (
                              <span className="ml-1 text-xs text-amber-600">
                                (var.)
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 truncate">
                            {inst.fechaPagoExacta
                              ? new Date(
                                  inst.fechaPagoExacta
                                ).toLocaleDateString("es-PE")
                              : "—"}
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                className="w-full min-w-0 border border-gray-300 rounded px-2 py-1 text-xs"
                                value={inst.montoReal ?? 0}
                                onChange={(e) => {
                                  const val = Number(e.target.value);
                                  setInstancias((prev) =>
                                    prev.map((i) =>
                                      i.id === inst.id
                                        ? { ...i, montoReal: val }
                                        : i
                                    )
                                  );
                                }}
                                onBlur={(e) => {
                                  handleMontoChange(inst, Number(e.target.value));
                                }}
                              />
                              <span className="text-xs text-gray-500 shrink-0">
                                {monedaLabel[moneda]}
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <select
                              value={inst.estadoPago}
                              onChange={(e) =>
                                handleEstadoChange(inst, e.target.value)
                              }
                              className={`w-full border rounded px-2 py-1 text-xs font-medium cursor-pointer ${
                                inst.estadoPago === "Pagado"
                                  ? "border-green-300 bg-green-50 text-green-800"
                                  : "border-red-300 bg-red-50 text-red-800"
                              }`}
                            >
                              <option value="Pendiente">Pendiente</option>
                              <option value="Pagado">Pagado</option>
                            </select>
                          </td>
                          <td className="px-3 py-2 truncate">
                            {getPlantillaLink(inst) ? (
                              <a
                                href={getPlantillaLink(inst)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 inline-flex items-center gap-1 text-xs"
                              >
                                Abrir <FaExternalLinkAlt />
                              </a>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-500 truncate">
                            {inst.fechaPagoReal
                              ? new Date(
                                  inst.fechaPagoReal
                                ).toLocaleDateString("es-PE")
                              : "—"}
                          </td>
                          <td className="px-3 py-2">
                            <DeleteButton
                              content="Eliminar instancia"
                              action={() => deleteInstancia(inst)}
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

export default PagosRecurrentes;
